import React, { useRef, useEffect, useState } from "react";
import "./ChatbotModal.css";
import "./ChatResponsive.css";
import { useAppDispatch, useAppSelector } from "../../store";
import { checkAddress, clearChatData } from "../../store/slices/chatSlice";
import ChatMessageList from "./ChatMessageList";
import ChatHeader from "./ChatHeader";
import ChatInput from "./ChatInput";
import InstructionalPanel from "./InstructionalPanel";
import { useChatState } from "./ChatState";
import { handleUserNameInput } from "./UserNameHandler";
import { useAddressHandler } from "./AddressHandler";
import { useFeedbackHandler } from "./FeedbackHandler";
import { useMessageHandlers } from "../../hooks/useMessageHandlers";
import { chatApiService } from "../../services/chatApiService";
import vector from "../../assets/images/Vector.png";
import { getCityName } from "../../utils/dynamicCityName";

interface MaximizedChatConversationProps {
  isVisible: boolean;
  onBackClick: () => void;
  onTypingStateChange: (isTyping: boolean) => void;
  showInstructionalPanel?: boolean;
  onToggleInstructionalPanel?: () => void;
  chatState?: ReturnType<typeof useChatState>;
  textSize?: number;
  onResetChat?: () => void;
   isMaximized?: boolean;
}

const MaximizedChatConversation: React.FC<MaximizedChatConversationProps> = ({
  isVisible,
  onBackClick,
  onTypingStateChange,
  showInstructionalPanel = false, // Panel should start closed
  onToggleInstructionalPanel,
  chatState: externalChatState,
  textSize = 100,
  onResetChat,
  isMaximized = true,
}) => {
  const dispatch = useAppDispatch();
  const {
    responseMessage,
    loading,
    error,
    propertyData,
    dayNight,
    reportLink,
  } = useAppSelector((state) => state.chat);
  const localChatState = useChatState();
  const chatState = externalChatState || localChatState;
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const [internalShowInstructionalPanel, setInternalShowInstructionalPanel] = useState(true);
  const { handleAddressInput } = useAddressHandler();
  const { handleFeedbackClick } = useFeedbackHandler();
  const [hasAIResponded, setHasAIResponded] = useState(false);
  const [isActiveComponent, setIsActiveComponent] = useState(false);

  // Auto-close instructional panel after 10 seconds
  useEffect(() => {
    const timer = setTimeout(() => {
      setInternalShowInstructionalPanel(false);
    }, 10000); // 10 seconds

    // Cleanup timer if component unmounts
    return () => clearTimeout(timer);
  }, []); // Empty dependency array means this runs once when component mounts
  
  // // Always keep instructional panel open - remove the state management
  // const internalShowInstructionalPanel = true; // Always true

  // Remove the toggle functionality since panel should always be open
  const handleToggleInstructionalPanel = () => {
    // Do nothing - panel should always stay open
    // if (onToggleInstructionalPanel) {
    //   onToggleInstructionalPanel();
    // }
  };

const handleVectorClick = () => {
  console.log('Vector clicked! Current state:', internalShowInstructionalPanel);
  setInternalShowInstructionalPanel(!internalShowInstructionalPanel);
  console.log('Setting state to:', !internalShowInstructionalPanel);
};

  const messageHandlers = useMessageHandlers(
    chatState.messages,
    chatState.setMessages,
    chatState.userName,
    chatState.setUserName,
    chatState.currentOption,
    chatState.setCurrentOption,
    chatState.previousOption,
    chatState.setPreviousOption,
    chatState.selectedOptions,
    chatState.setSelectedOptions,
    chatState.setIsTyping,
    chatState.setAddressConfirmed,
    chatState.setShowFollowUpOptions,
    chatState.setShowFeedback
  );

  useEffect(() => {
    if (onTypingStateChange) {
      onTypingStateChange(chatState.isTyping);
    }
  }, [chatState.isTyping, onTypingStateChange]);

  useEffect(() => {
    if (isVisible) {
      setIsActiveComponent(true);
    } else {
      setIsActiveComponent(false);
    }
  }, [isVisible]);

  // Remove the timer effect that was delaying the panel opening
  // useEffect(() => {
  //   let openTimer: NodeJS.Timeout;
  //   if (isVisible && !internalShowInstructionalPanel) {
  //     openTimer = setTimeout(() => {
  //       setInternalShowInstructionalPanel(true);
  //     }, 100);
  //   }
  //   return () => {
  //     clearTimeout(openTimer);
  //   };
  // }, [isVisible]);

  const handleSendMessage = async () => {
    if (chatState.inputValue.trim()) {
      const userMessage = chatState.inputValue;
      chatState.setMessages((prev) => [
        ...prev,
        { text: userMessage, isUser: true },
      ]);
      chatState.setInputValue("");
      chatState.setIsTyping(true);
      
      // Handle first message as name input
      const isFirstMessage = !chatState.userName && chatState.messages.length === 1;
      if (isFirstMessage) {
        const handled = handleUserNameInput({
          userMessage,
          messages: chatState.messages,
          setUserName: chatState.setUserName,
          setIsTyping: chatState.setIsTyping,
          setMessages: chatState.setMessages,
          setShowFollowUpOptions: chatState.setShowFollowUpOptions,
        });
        if (handled) return;
      }

      // Handle farewell messages to show feedback - only exact matches
      const farewellKeywords = ['no', 'thank you', 'thanks', 'bye', 'goodbye', 'close this', 'end', 'stop', 'quit', 'exit'];
      const normalizedMessage = userMessage.toLowerCase().trim();
      const isFarewellMessage = farewellKeywords.some(keyword => 
        normalizedMessage === keyword
      );
      
      if (isFarewellMessage) {
        setTimeout(() => {
          chatState.setIsTyping(false);
          chatState.setMessages((prev) => [
            ...prev,
            {
              text: `It was my pleasure assisting you today${
                chatState.userName
                  ? ", <strong>" + chatState.userName + "</strong>"
                  : ""
              }. The ${getCityName()} would appreciate it if you could rate your experience.`,
              isUser: false,
              showFeedbackUI: true,
            },
          ]);
          chatState.setShowFeedback(true);
        }, 1500);
        return;
      }

      try {
        // Call the new API for all messages after name input
        const response = await chatApiService.sendMessage(userMessage);
        
        setTimeout(() => {
          chatState.setIsTyping(false);
          chatState.setMessages((prev) => [
            ...prev,
            {
              text: response.response,
              isUser: false,
              pdfUrl: response.pdf_url, // Store PDF URL if provided
            },
          ]);
          
          // Only show follow-up options if the response is not asking for more input
          const responseText = response.response.toLowerCase();
          const isAskingForInput = 
            // Direct question indicators
            response.response.includes('?') ||
            // Question starters
            responseText.startsWith('could you') ||
            responseText.startsWith('can you') ||
            responseText.startsWith('would you') ||
            responseText.startsWith('what') ||
            responseText.startsWith('which') ||
            responseText.startsWith('how') ||
            responseText.startsWith('when') ||
            responseText.startsWith('where') ||
            responseText.startsWith('why') ||
            // Question phrases
            responseText.includes('could you please') ||
            responseText.includes('can you please') ||
            responseText.includes('would you please') ||
            responseText.includes('please provide') ||
            responseText.includes('please specify') ||
            responseText.includes('please tell') ||
            responseText.includes('what type of') ||
            responseText.includes('which type of') ||
            responseText.includes('what kind of') ||
            responseText.includes('are you looking for') ||
            responseText.includes('are you interested in') ||
            responseText.includes('are you considering') ||
            responseText.includes('would like to check') ||
            responseText.includes('inquiring about') ||
            responseText.includes('asking about');
          
          // Check if user has provided a valid address (based on response content)
          const hasAddressInfo = response.response.toLowerCase().includes('site at') ||
                                response.response.toLowerCase().includes('property') ||
                                response.response.toLowerCase().includes('city limits') ||
                                response.response.toLowerCase().includes('zone') ||
                                response.response.toLowerCase().includes('zoning');
          
          if (hasAddressInfo && !isAskingForInput) {
            // Mark that user has provided an address
            chatState.setHasProvidedAddress(true);
          }
          
          // Show PDF option if PDF URL is provided
          if (response.pdf_url) {
            chatState.setShowPdfOption(true);
            // Add PDF message index
            setTimeout(() => {
              if (chatState.setPdfMessageIndices) {
                chatState.setPdfMessageIndices((prev) => [
                  ...prev,
                  chatState.messages.length, // Index of the message we just added
                ]);
              }
            }, 100);
          }
          
          if (!isAskingForInput) {
            setTimeout(() => {
              chatState.setMessages((prev) => [
                ...prev,
                {
                  text: `Below are additional questions that you could answer or type <strong>No</strong> to end this conversation`,
                  isUser: false,
                  showFollowUpButtons: true,
                  isFollowUpMessage: true,
                },
              ]);
              chatState.setShowFollowUpOptions(true);
            }, 1000);
          }
        }, 1500);
        
      } catch (error) {
        console.error('Error calling chat API:', error);
        setTimeout(() => {
          chatState.setIsTyping(false);
          chatState.setMessages((prev) => [
            ...prev,
            {
              text: "I'm sorry, I'm having trouble connecting right now. Please try again later.",
              isUser: false,
            },
          ]);
        }, 1500);
      }
    }
  };

  const handleOptionClick = async (optionText: string) => {
    // Handle special case for "No" to end conversation
    if (optionText.toLowerCase() === "no") {
      chatState.setMessages((prev) => [
        ...prev,
        { text: optionText, isUser: true, isOption: true },
      ]);
      
      setTimeout(() => {
        chatState.setIsTyping(false);
        chatState.setMessages((prev) => [
          ...prev,
          {
            text: `It was my pleasure assisting you today${
              chatState.userName
                ? ", <strong>" + chatState.userName + "</strong>"
                : ""
            }. The city of Mayorville would appreciate it if you could rate your experience.`,
            isUser: false,
          },
        ]);
        chatState.setShowFeedback(true);
      }, 1500);
      return;
    }

    // Handle special case for "Change address" to reset conversation
    if (optionText.toLowerCase() === "change address") {
      chatState.setMessages((prev) => [
        ...prev,
        { text: optionText, isUser: true, isOption: true },
      ]);
      
      setTimeout(() => {
        chatState.setIsTyping(false);
        chatState.setMessages((prev) => [
          ...prev,
          {
            text: "Please provide the new address you'd like to check.",
            isUser: false,
          },
        ]);
        // Reset address-related state but keep the conversation going
        chatState.setHasProvidedAddress(false);
        chatApiService.resetConversation(); // Reset conversation ID for new address
      }, 1500);
      return;
    }

    // For all other options, use the new API
    chatState.setMessages((prev) => [
      ...prev,
      { text: optionText, isUser: true, isOption: true },
    ]);
    chatState.setIsTyping(true);

    try {
      const response = await chatApiService.sendMessage(optionText);
      
      setTimeout(() => {
        chatState.setIsTyping(false);
        chatState.setMessages((prev) => [
          ...prev,
          {
            text: response.response,
            isUser: false,
            pdfUrl: response.pdf_url,
          },
        ]);
        
        // Only show follow-up options if the response is not asking for more input
        const responseText = response.response.toLowerCase();
        const isAskingForInput = 
          // Direct question indicators
          response.response.includes('?') ||
          // Question starters
          responseText.startsWith('could you') ||
          responseText.startsWith('can you') ||
          responseText.startsWith('would you') ||
          responseText.startsWith('what') ||
          responseText.startsWith('which') ||
          responseText.startsWith('how') ||
          responseText.startsWith('when') ||
          responseText.startsWith('where') ||
          responseText.startsWith('why') ||
          // Question phrases
          responseText.includes('could you please') ||
          responseText.includes('can you please') ||
          responseText.includes('would you please') ||
          responseText.includes('please provide') ||
          responseText.includes('please specify') ||
          responseText.includes('please tell') ||
          responseText.includes('what type of') ||
          responseText.includes('which type of') ||
          responseText.includes('what kind of') ||
          responseText.includes('are you looking for') ||
          responseText.includes('are you interested in') ||
          responseText.includes('are you considering') ||
          responseText.includes('would like to check') ||
          responseText.includes('inquiring about') ||
          responseText.includes('asking about');
        
        // Check if user has provided a valid address (based on response content)
        const hasAddressInfo = response.response.toLowerCase().includes('site at') ||
                              response.response.toLowerCase().includes('property') ||
                              response.response.toLowerCase().includes('city limits') ||
                              response.response.toLowerCase().includes('zone') ||
                              response.response.toLowerCase().includes('zoning');
        
        if (hasAddressInfo && !isAskingForInput) {
          // Mark that user has provided an address
          chatState.setHasProvidedAddress(true);
        }
        
        // Show PDF option if PDF URL is provided
        if (response.pdf_url) {
          chatState.setShowPdfOption(true);
          setTimeout(() => {
            if (chatState.setPdfMessageIndices) {
              chatState.setPdfMessageIndices((prev) => [
                ...prev,
                chatState.messages.length,
              ]);
            }
          }, 100);
        }
        
        if (!isAskingForInput) {
          setTimeout(() => {
            chatState.setMessages((prev) => [
              ...prev,
              {
                text: `Below are additional questions that you could answer or type <strong>No</strong> to end this conversation`,
                isUser: false,
                showFollowUpButtons: true,
                isFollowUpMessage: true,
              },
            ]);
            chatState.setShowFollowUpOptions(true);
          }, 1000);
        }
      }, 1500);
      
    } catch (error) {
      console.error('Error calling chat API for option:', error);
      setTimeout(() => {
        chatState.setIsTyping(false);
        chatState.setMessages((prev) => [
          ...prev,
          {
            text: "I'm sorry, I'm having trouble connecting right now. Please try again later.",
            isUser: false,
          },
        ]);
      }, 1500);
    }
  };

  const handleFeedbackSubmit = (rating: number) => {
    handleFeedbackClick(rating, {
      userName: chatState.userName,
      setMessages: chatState.setMessages,
      setAddressConfirmed: chatState.setAddressConfirmed,
      resetChat: chatState.resetChat,
      onBackClick,
    });
  };
  const resetChatState = () => {
    // Reset the conversation ID for new chat
    chatApiService.resetConversation();
    
    if (onResetChat) {
      onResetChat();
    } else {
      chatState.resetChat();
    }
    dispatch(clearChatData());
  };

  const scrollToBottom = () => {
    try {
      // Use requestAnimationFrame to ensure DOM is fully rendered
      requestAnimationFrame(() => {
        // Method 1: Try scroll anchor (most reliable)
        if (messagesEndRef.current) {
          messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
          return;
        }
        
        // Method 2: Try ref
        if (chatContainerRef.current) {
          chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
          return;
        }
        
        // Method 3: Try querySelector as fallback
        const chatInstructions = document.querySelector(".chat-message-instructions") as HTMLElement;
        if (chatInstructions) {
          chatInstructions.scrollTop = chatInstructions.scrollHeight;
        }
      });
    } catch (error) {
      console.error("Error scrolling to bottom:", error);
    }
  };
  const [lastProcessedResponse, setLastProcessedResponse] = useState<
    string | null
  >(null);
  const [followUpMessageAdded, setFollowUpMessageAdded] =
    useState<boolean>(false);
  const [processedPdfIndices, setProcessedPdfIndices] = useState<number[]>([]);
  useEffect(() => {
    const lastMessage = chatState.messages[chatState.messages.length - 1];
    if (lastMessage && lastMessage.isUser) {
      setFollowUpMessageAdded(false);
    }
  }, [chatState.messages]);

  useEffect(() => {
    if (isVisible) {
      if (chatState.pdfMessageIndices.length === 0) {
        chatState.setPdfMessageIndices([]);
      }
      if (chatState.messages.length === 0) {
        chatState.setShowPdfOption(false);
      }
    }
  }, [isVisible]);

  useEffect(() => {
    if (!isActiveComponent) return;
    if (chatState.addressConfirmed && !loading) {
      chatState.setIsTyping(false);
      chatState.setAddressConfirmed(false);
      if (error) {
        chatState.setMessages((prev) => [
          ...prev,
          { text: error, isUser: false },
        ]);
        setTimeout(() => {
          chatState.setSelectedOptions(new Set());
          chatState.setShowFollowUpOptions(true);
          chatState.setMessages((prev) => [
            ...prev,
            {
              text: `Below are additional questions that you could answer or type <strong>No</strong> to end this conversation`,
              isUser: false,
              showFollowUpButtons: true,
              isFollowUpMessage: true, 
            },
          ]);
        }, 1000);
      } else if (responseMessage && propertyData) {
        setLastProcessedResponse(responseMessage);
        chatState.setMessages((prev) => {
          const newMessages = [...prev];
          const lastMessage = newMessages[newMessages.length - 1];
          if (lastMessage && lastMessage.text === "Sure. Let me check this.") {
            newMessages[newMessages.length - 1] = {
              text: responseMessage,
              isUser: false,
            };
            setTimeout(() => {
              if (chatState.setPdfMessageIndices) {
                chatState.setPdfMessageIndices((prev) => [
                  ...prev,
                  newMessages.length - 1,
                ]);
              }
            }, 100);
            return newMessages;
          } else {
            setTimeout(() => {
              if (chatState.setPdfMessageIndices) {
                chatState.setPdfMessageIndices((prev) => [
                  ...prev,
                  prev.length,
                ]);
              }
            }, 100);

            return [...prev, { text: responseMessage, isUser: false }];
          }
        });
        chatState.setShowPdfOption(true);
        chatState.setIsTyping(false);
        if (!followUpMessageAdded) {
          setTimeout(() => {
            if (isActiveComponent) {
              chatState.setMessages((prev) => [
                ...prev,
                {
                  text: `Below are additional questions that you could answer or type <strong>No</strong> to end this conversation`,
                  isUser: false,
                  showFollowUpButtons: true,
                  isFollowUpMessage: true, 
                },
              ]);
              chatState.setShowFollowUpOptions(true);
            }
            setFollowUpMessageAdded(true);
          }, 1000);
        }
      } else if (chatState.addressConfirmed && loading) {
        chatState.setIsTyping(true);
      }
    }
  }, [
    loading,
    error,
    responseMessage,
    propertyData,
    chatState.addressConfirmed,
    isActiveComponent,
    followUpMessageAdded,
    lastProcessedResponse,
  ]);

  useEffect(() => {
    const setContainerHeightByZoom = () => {
      try {
        const zoomLevel1 = Math.round(window.devicePixelRatio * 100);
        const zoomLevel2 = Math.round((window.outerWidth / window.innerWidth) * 100);
        const zoomLevel = zoomLevel1; // Use devicePixelRatio as primary method
        console.log('Container zoom detection - devicePixelRatio:', zoomLevel1, 'outerWidth/innerWidth:', zoomLevel2);
        const chatContainer = document.querySelector('.chat-conversation-container.maximized') as HTMLElement;
      } catch (error) {
        console.error('Error in setContainerHeightByZoom:', error);
      }
    };
    
    const initialTimer = setTimeout(() => {
      setContainerHeightByZoom();
    }, 100);
    
    window.addEventListener('resize', setContainerHeightByZoom);
    window.addEventListener('orientationchange', setContainerHeightByZoom);
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', setContainerHeightByZoom);
    }
    return () => {
      clearTimeout(initialTimer);
      window.removeEventListener('resize', setContainerHeightByZoom);
      window.removeEventListener('orientationchange', setContainerHeightByZoom);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', setContainerHeightByZoom);
      }
    };
  }, [isVisible, internalShowInstructionalPanel]);

  useEffect(() => {
    // Increased delay for embedded iframe - DOM takes longer to render
    const timer = setTimeout(() => {
      scrollToBottom();
    }, 300);
    
    // Add a retry after additional delay if first attempt fails
    const retryTimer = setTimeout(() => {
      scrollToBottom();
    }, 600);
    
    return () => {
      clearTimeout(timer);
      clearTimeout(retryTimer);
    };
  }, [chatState.messages, chatState.isTyping]);

  useEffect(() => {
    const hasAIMessages = chatState.messages.some((message) => !message.isUser);
    if (hasAIMessages) {
      setHasAIResponded(true);
    }
  }, [chatState.messages]);

  if (!isVisible) return null;
  return (
    <div className="chat-conversation-partent">
      <div
        style={{
          backgroundColor: "white",
          borderRadius: "8px",
        }}
        className={`chat-conversation-container maximized text-size-${textSize}`}
      >
        <ChatHeader
          onBackClick={onBackClick}
          onResetChat={resetChatState}
          onToggleInstructionalPanel={handleToggleInstructionalPanel}
          isMaximized={true}
          title={`Chat with ${getCityName()}'s AI Companion`}
        />
        <div 
        className="chat-main-content with-sidebar"
        >
          <div className="chat-messages-section">
            <div
              className="chat-messages-container"
              style={{
                flex: 1,
                overflowY: "hidden",
              }}
            >
              <div
                className={`chat-message-instructions ${
                  !internalShowInstructionalPanel ? "no-panel" : ""
                }`}
                ref={chatContainerRef}
              >
                <div style={{ marginTop: "15px" }}>
                  <ChatMessageList
                    messages={chatState.messages}
                    userName={chatState.userName}
                    feedbackRating={chatState.feedbackRating}
                    showPdfOption={chatState.showPdfOption}
                    showFollowUpOptions={chatState.showFollowUpOptions}
                    showZoningOptions={chatState.showZoningOptions}
                    pdfMessageIndices={chatState.pdfMessageIndices}
                    currentOption={chatState.currentOption}
                    reportLink={reportLink}
                    propertyData={propertyData}
                    selectedOptions={chatState.selectedOptions}
                    dayNight={dayNight || "day"}
                    isTyping={chatState.isTyping}
                    isMaximized={true}
                    followUpMessageAdded={followUpMessageAdded} 
                    onOptionClick={handleOptionClick}
                    onFeedbackClick={handleFeedbackSubmit}
                    onBackClick={onBackClick}
                  />
                  <div
                    ref={messagesEndRef}
                    style={{ height: "1px", width: "100%" }}
                  />
                </div>
              </div>
            </div>
            <div
              className="chat-footer"
              style={{
                position: "sticky",
                bottom: 0,
                // backgroundColor: "white",
                borderTop: "1px solid #e0e0e0",
                marginTop: "auto",
              }}
            >
              <ChatInput
                inputValue={chatState.inputValue}
                setInputValue={chatState.setInputValue}
                onSendMessage={handleSendMessage}
                showFeedback={chatState.showFeedback}
                isVisible={isVisible}
                isMaximized={true}
                onOptionClick={handleOptionClick}
                hasAIResponded={hasAIResponded}
                textSize={textSize}
                propertyData={propertyData}
                isInstructionalPanelOpen={internalShowInstructionalPanel}
                hasProvidedAddress={chatState.hasProvidedAddress}
              />
            </div>
          </div>
          {/* Restore conditional rendering for collapse/expand functionality */}
          <div
            className={`chat-instructional-sidebar ${
              internalShowInstructionalPanel ? "expanded" : "collapsed"
            }`}
          >
            {!internalShowInstructionalPanel ? (
              <div className="maximize-base" onClick={handleVectorClick} style={{ marginTop: "6px" }}>
                <img src={vector} alt="help"  className="vector-img" />
              </div>
            ) : (
              <>
                <InstructionalPanel
                  isOpen={internalShowInstructionalPanel}
                  onClose={() => setInternalShowInstructionalPanel(false)}
                  isInternal={true}
                  isMaximized={true}
                />
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MaximizedChatConversation;